/**
 * Portal V2 feature gate and route helpers.
 * Classic routes stay the default until usePortalV2 is enabled at runtime
 * OR the user enters via a V2 login (session).
 *
 * Enable via public/Config.js:
 *   window.APP_CONFIG = { ..., usePortalV2: true }
 * Or: window.usePortalV2 = true
 * Or: login through /login-v2 (sets sessionStorage)
 */

const SESSION_KEY = 'usePortalV2';

const V2_PATHS = {
    customize: '/customizeQuestions-v2',
    steps: '/steps-v2',
    dashboard: '/dashboard-v2',
    nonSubscriber: '/nonSubscriber-v2',
    login: '/login-v2',
};

const CLASSIC_PATHS = {
    customize: '/customizeQuestions',
    steps: '/steps',
    dashboard: '/dashboard',
    nonSubscriber: '/NonSubscriber',
    login: '/login',
};

function readConfigFlag() {
    if (typeof window === 'undefined') return false;
    if (window.usePortalV2 === true) return true;
    if (window.APP_CONFIG && window.APP_CONFIG.usePortalV2 === true) return true;
    return false;
}

function readSessionFlag() {
    if (typeof window === 'undefined' || !window.sessionStorage) return false;
    try {
        return window.sessionStorage.getItem(SESSION_KEY) === 'true';
    } catch {
        return false;
    }
}

/** Mark this browser session as Portal V2 (e.g. after /login-v2). */
export function enablePortalV2Session() {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    try {
        window.sessionStorage.setItem(SESSION_KEY, 'true');
    } catch {
        /* ignore */
    }
}

export function isPortalV2Enabled() {
    return readConfigFlag() || readSessionFlag();
}

/**
 * Soft-launch guard: V2 shells redirect to classic unless the global flag
 * or a /login-v2 session is active. Keeps V1 as the default in prod.
 * @returns {boolean} true when the V2 route may render
 */
export function guardPortalV2Route(history, classicFallback = CLASSIC_PATHS.dashboard) {
    if (isPortalV2Enabled()) return true;
    if (history && typeof history.replace === 'function') {
        history.replace(classicFallback);
    }
    return false;
}

/** Always V2 routes — use inside Portal V2 screens. */
export function getPortalV2Paths() {
    return { ...V2_PATHS };
}

/** Post-login / post-step destinations (classic vs V2). */
export function getPortalPaths() {
    return isPortalV2Enabled() ? { ...V2_PATHS } : { ...CLASSIC_PATHS };
}

export function resolveUserStepPath(userStep) {
    const paths = getPortalPaths();
    const map = {
        0: paths.customize,
        1: paths.steps,
        2: paths.steps,
        3: paths.steps,
        4: paths.dashboard,
        5: paths.dashboard,
    };
    return map[userStep] || paths.steps;
}

/** Resolve step destination forcing V2 routes (login-v2 / Portal V2 shell). */
export function resolveUserStepPathV2(userStep) {
    const paths = V2_PATHS;
    const map = {
        0: paths.customize,
        1: paths.steps,
        2: paths.steps,
        3: paths.steps,
        4: paths.dashboard,
        5: paths.dashboard,
    };
    return map[userStep] || paths.steps;
}

export function resolvePostOnboardingDashboard() {
    return getPortalPaths().dashboard;
}

export function resolveNonSubscriberPath() {
    return getPortalPaths().nonSubscriber;
}

export function resolveNonSubscriberPathV2() {
    return V2_PATHS.nonSubscriber;
}
